import { StandardTabs } from '@cimplico/components';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Firm } from '../../../types/firm';
import { useIsFirmRole } from '../../../hooks/use-is-firm-role';
import FirmDetailsContainer from './details/FirmDetailsContainer';
import FirmBillingSubscriptionContainer from './billing-subscription/FirmBillingSubscriptionContainer';
import FirmUsersContainer from './users/FirmUsersContainer';
import FirmDescriptionsContainer from './quick-description/FirmDescriptionsContainer';
import FirmIntegrationsContainer from './integrations/FirmIntegrationsContainer';
import FirmInvitationsContainer from './invitations/FirmInvitationsContainer';
import BillingV2 from './billing-v2/BillingV2';

type FirmSettingsProps = {
    firm: Firm;
};

function getTabs({ isAdmin, isManager }: { isAdmin?: boolean; isManager?: boolean }): string[] {
    if (isAdmin) {
        return ['details', 'billing-subscription', 'users', 'invitations', 'quick-descriptions', 'integrations'];
    } else if (isManager) {
        return ['details', 'users', 'invitations', 'quick-descriptions'];
    } else {
        return ['details', 'users', 'invitations'];
    }
}

function FirmSettings({ firm }: FirmSettingsProps): JSX.Element {
    const { isAdmin, isManager, isUser } = useIsFirmRole();
    const tabs = getTabs({ isAdmin, isManager });

    const [params, setParams] = useSearchParams();
    const [tab, setTab] = useState(getSavedTab(params.get('tab')));

    function getSavedTab(tab: string | null): number {
        if (!tab) {
            return 0;
        }

        return tabs.includes(tab) ? tabs.indexOf(tab) : 0;
    }

    function handleTabChange(t: number): void {
        setTab(t);
        setParams({ tab: tabs[t] });
    }

    return (
        <>
            <div className="border border-gray-200 dark:border-white/[0.07] rounded-lg w-full pb-4 grid gap-y-4 bg-white dark:bg-gray-800">
                <div className="border-b dark:border-white/[0.07] px-8">
                    <StandardTabs
                        tabs={
                            isAdmin
                                ? [
                                      { label: 'Details' },
                                      { label: 'Billing & Subscription' },
                                      { label: 'Users' },
                                      { label: 'Invitations' },
                                      { label: 'Quick descriptions' },
                                      { label: 'Integrations' },
                                      // { label: 'Automations' },
                                  ]
                                : isManager
                                ? [{ label: 'Details' }, { label: 'Users' }, { label: 'Invitations' }, { label: 'Quick descriptions' }]
                                : [{ label: 'Details' }, { label: 'Users' }, { label: 'Invitations' }]
                        }
                        onChange={handleTabChange}
                        defaultIndex={tab}
                    />
                </div>
                <div className="px-8">
                    {isAdmin && (
                        <>
                            {tab === 0 && <FirmDetailsContainer firm={firm} />}
                            {tab === 1 && (firm.subscriptionVersion === 1 ? <FirmBillingSubscriptionContainer /> : <BillingV2 />)}
                            {tab === 2 && <FirmUsersContainer />}
                            {tab === 3 && <FirmInvitationsContainer />}
                            {tab === 4 && <FirmDescriptionsContainer />}
                            {tab === 5 && <FirmIntegrationsContainer />}
                        </>
                    )}
                    {(isManager || isUser) && (
                        <>
                            {tab === 0 && <FirmDetailsContainer firm={firm} />}
                            {tab === 1 && <FirmUsersContainer />}
                            {tab === 2 && <FirmInvitationsContainer />}
                            {tab === 3 && isManager && <FirmDescriptionsContainer />}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default FirmSettings;
